import { formatMetricaDate } from '../utils/dateUtils';

export type RecurrenceFrequency = 'weekly' | 'monthly' | 'yearly';
export type RecurrenceWeekDay = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';
export type RecurrenceEndType = 'byDate' | 'afterCount' | 'noEnd';
export const MAX_RECURRENCE_COUNT = 120;
export const RECURRENCE_HORIZON_YEARS = 2;
export type RecurrenceNthWeek = 1 | 2 | 3 | 4 | -1;

export interface RecurrenceConfig {
  frequency: RecurrenceFrequency;
  interval: number;
  weekDay: RecurrenceWeekDay;
  monthlyType: 'dayOfMonth' | 'nthWeekday';
  monthDay: number;
  nthWeek: RecurrenceNthWeek;
  nthWeekDay: RecurrenceWeekDay;
  yearlyType: 'date' | 'nthWeekday';
  yearMonth: number;
  startDate: string;
  endType: RecurrenceEndType;
  endDate: string;
  count: number;
}

export const WEEK_DAYS: { id: RecurrenceWeekDay; label: string }[] = [
  { id: 'sun', label: 'יום ראשון' },
  { id: 'mon', label: 'יום שני' },
  { id: 'tue', label: 'יום שלישי' },
  { id: 'wed', label: 'יום רביעי' },
  { id: 'thu', label: 'יום חמישי' },
  { id: 'fri', label: 'יום שישי' },
  { id: 'sat', label: 'יום שבת' },
];

export const NTH_WEEK_OPTIONS: { value: RecurrenceNthWeek; label: string }[] = [
  { value: 1, label: 'הראשון' },
  { value: 2, label: 'השני' },
  { value: 3, label: 'השלישי' },
  { value: 4, label: 'הרביעי' },
  { value: -1, label: 'האחרון' },
];

export const HEBREW_MONTHS = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
];

export const daysInMonth = (month: number, year: number): number =>
  new Date(year, month, 0).getDate();

export const yearFromIsoDate = (value?: string): number => parseIsoDate(value).getFullYear();

const WEEK_DAY_BY_INDEX: RecurrenceWeekDay[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

const toIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseIsoDate = (value?: string): Date => {
  if (value && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [year, month, day] = value.split('-').map((part) => parseInt(part, 10));
    return new Date(year, month - 1, day);
  }
  return new Date();
};

const addYearsClamped = (date: Date, years: number): Date => {
  const preferredDay = date.getDate();
  const result = new Date(date.getFullYear() + years, date.getMonth(), 1);
  result.setDate(Math.min(preferredDay, daysInMonth(result.getMonth() + 1, result.getFullYear())));
  return result;
};

const addMonthsClamped = (date: Date, months: number): Date => {
  const preferredDay = date.getDate();
  const result = new Date(date.getFullYear(), date.getMonth() + months, 1);
  result.setDate(Math.min(preferredDay, daysInMonth(result.getMonth() + 1, result.getFullYear())));
  return result;
};

export const exceedsTwoYearHorizon = (config: RecurrenceConfig): boolean => {
  const start = parseIsoDate(config.startDate);
  const horizonIso = toIsoDate(addYearsClamped(start, RECURRENCE_HORIZON_YEARS));

  if (config.endType === 'byDate') {
    if (!config.endDate || !/^\d{4}-\d{2}-\d{2}/.test(config.endDate)) {
      return true;
    }
    return config.endDate.slice(0, 10) > horizonIso;
  }

  const steps = Math.max(1, Number(config.count) || 1) - 1;
  let end = new Date(start);
  if (config.frequency === 'weekly') {
    end.setDate(end.getDate() + steps * 7);
  } else if (config.frequency === 'yearly') {
    end = addYearsClamped(start, steps);
  } else {
    end = addMonthsClamped(start, steps);
  }
  return toIsoDate(end) > horizonIso;
};

export const defaultRecurrenceConfig = (startDate?: string): RecurrenceConfig => {
  const date = parseIsoDate(startDate);
  const weekDay = WEEK_DAY_BY_INDEX[date.getDay()];
  return {
    frequency: 'weekly',
    interval: 1,
    weekDay,
    monthlyType: 'dayOfMonth',
    monthDay: date.getDate(),
    nthWeek: 1,
    nthWeekDay: weekDay,
    yearlyType: 'date',
    yearMonth: date.getMonth() + 1,
    startDate: startDate && /^\d{4}-\d{2}-\d{2}/.test(startDate) ? startDate.slice(0, 10) : toIsoDate(date),
    endType: 'byDate',
    endDate: '',
    count: 12,
  };
};

const PY_WEEKDAY: Record<RecurrenceWeekDay, number> = {
  mon: 0,
  tue: 1,
  wed: 2,
  thu: 3,
  fri: 4,
  sat: 5,
  sun: 6,
};

const toPyWeekday = (date: Date) => (date.getDay() + 6) % 7;

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(date.getDate() + days);
  return next;
};

/** First date the server would create. Matches the repeat rules already in use. */
export const firstRecurrenceDate = (config: RecurrenceConfig): Date => {
  const start = parseIsoDate(config.startDate);
  if (config.frequency === 'weekly') {
    const target = PY_WEEKDAY[config.weekDay] ?? toPyWeekday(start);
    const delta = (target - toPyWeekday(start) + 7) % 7;
    return addDays(start, delta);
  }
  if (config.frequency === 'yearly') {
    const month = Math.max(1, Math.min(Number(config.yearMonth) || start.getMonth() + 1, 12));
    const preferred = Math.max(1, Math.min(Number(config.monthDay) || start.getDate(), 31));
    const inStartYear = new Date(start.getFullYear(), month - 1, 1);
    inStartYear.setDate(Math.min(preferred, daysInMonth(month, start.getFullYear())));
    if (inStartYear >= start) return inStartYear;
    const nextYear = new Date(start.getFullYear() + 1, month - 1, 1);
    nextYear.setDate(Math.min(preferred, daysInMonth(month, start.getFullYear() + 1)));
    return nextYear;
  }
  const preferred = Math.max(1, Math.min(Number(config.monthDay) || start.getDate(), 31));
  const thisMonth = new Date(start.getFullYear(), start.getMonth(), 1);
  thisMonth.setDate(Math.min(preferred, daysInMonth(start.getMonth() + 1, start.getFullYear())));
  if (thisMonth >= start) return thisMonth;
  const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
  nextMonth.setDate(Math.min(preferred, daysInMonth(nextMonth.getMonth() + 1, nextMonth.getFullYear())));
  return nextMonth;
};

/** What the user still has to fill before a repeat can be saved. */
export const recurrenceCompletionMessage = (config: RecurrenceConfig): string | null => {
  if (!config.startDate || !/^\d{4}-\d{2}-\d{2}/.test(config.startDate)) {
    return 'חסר תאריך התחלה לחזרה. מלאו את תאריך ההתחלה.';
  }
  if (config.endType !== 'byDate') return null;
  if (!config.endDate || !/^\d{4}-\d{2}-\d{2}/.test(config.endDate)) {
    return 'חסר תאריך סיום לחזרה. מלאו את תאריך הסיום.';
  }
  const first = firstRecurrenceDate(config);
  const end = parseIsoDate(config.endDate);
  if (first > end) {
    const shown = formatMetricaDate(toIsoDate(first));
    return `אין חזרה לפני \u2066${shown}\u2069. עדכנו את תאריך הסיום.`;
  }
  return null;
};
